import type { ReactNode } from 'react';
import { type QueryParamConfig } from 'use-query-params';
import type { FilterControl, FilterOptionsLoad } from '../../../types/filterControl';
import type { FilterSelectOption, QuickFilterOption, QuickFilterTuple } from '../../../types/table';
import { filterUrlState, type Stored, storedParam, storedValue, toStored, toUrlKey } from './filterSelection';
import { brand, type FilterUnit } from './filterUnitType';

/** Decoded URL values by URL key, as `useQueryParams` reads them. */
export type UrlValues = Record<string, unknown>;
type SetUrlValues = (changes: (latest: UrlValues) => UrlValues) => void;

/**
 * A filter a builder describes and `useFilterUnits` resolves: the URL keys it owns and the unit it makes of
 * their values. Not a hook, so every builder of a list shares one `useQueryParams`.
 */
export type FilterSpec<TParams, TControl extends FilterControl = FilterControl, TLink = object> = {
  readonly kind: 'filter-spec';
  /** Type-only: the control `controlOf` returns for this spec. */
  readonly control?: TControl;
  /**
   * Type-only: the params a link into the list may set (`filterSearch`). The URL side, which is not `TParams`
   * once `map` renames them (`numCarsGte` → `numCarsMin`). Default `object`: nothing, so a spec widened to
   * `FilterSpec<Query>` never claims the whole query.
   */
  readonly link?: TLink;
  /** Ids of the controls it renders, so `controlOf` finds them also inside `map`. */
  controlIds: readonly string[];
  /** Changes when the URL params decode differently (enum options), so their configs stay stable otherwise. */
  signature: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each spec owns its own value type
  urlParams: Record<string, QueryParamConfig<any, any>>;
  resolve: (values: UrlValues, setValues: SetUrlValues) => FilterUnit<TParams>;
};

/** A select option whose value is what the param takes, so a control cannot offer values of another kind. */
export type FilterOption<TValue extends string | number> = Omit<FilterSelectOption, 'value'> & { value: TValue };

declare const optionSourceBrand: unique symbol;

/**
 * Options loaded from an endpoint, with the load state and search the sidebar shows. Builders never fetch, and
 * only noddi-web's `toFilterOptionSource` builds one, from a `useFilterOptionsGet` query: options wait until a
 * sidebar shows them, and report `isLoading` (not `isPending`, which stays true for a query not yet enabled).
 */
export type FilterOptionSource<TValue extends string | number> = {
  options: FilterOption<TValue>[];
  load: FilterOptionsLoad;
  /** How URL values parse back: ids are numbers; cities and zip codes are strings even when they look numeric. */
  valueType: TValue extends number ? 'number' : 'string';
  readonly [optionSourceBrand]: true;
};

/** Static options, or options an endpoint loads. */
export type FilterOptionsInput<TValue extends string | number> =
  | { options: FilterOption<TValue>[]; source?: never }
  | { source: FilterOptionSource<TValue>; options?: never };

/**
 * A tray over a multi-select's own param: each option includes `values` (none = everything) and clears
 * exclusions; it is highlighted while the selection is exactly that.
 */
export type QuickFilterSpec<TValue extends string | number> = {
  label: string;
  options: QuickFilterTuple<QuickFilterOption & { values?: readonly TValue[] }>;
};

export type ControlText = { title: string; description?: ReactNode };

/** Any spec, whatever its control and link: for code that reads only its URL params and params. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the control and link are type-only
export type AnySpec = FilterSpec<object, any, any>;

export const isFilterSpec = (item: object): item is AnySpec => 'kind' in item && item.kind === 'filter-spec';

// --- Runtime -------------------------------------------------------------------------------------------------

export const excludedKeyOf = (key: string) => `excluded${key.charAt(0).toUpperCase()}${key.slice(1)}`;

export const nonEmpty = <T>(values: T[]) => (values.length > 0 ? values : undefined);

export const parseAll = <T>(values: (string | number)[], parse: (value: string) => T | undefined) =>
  values.flatMap((value) => {
    const parsed = parse(String(value));
    return parsed === undefined ? [] : [parsed];
  });

/**
 * Yes / No. `onlyTrue`: one checkbox that narrows to `true` and otherwise sends nothing, for flags whose `false`
 * is not a useful list.
 */
export const booleanSpec = ({
  key,
  defaultValue,
  onlyTrue,
  action
}: {
  key: string;
  defaultValue: boolean | undefined;
  onlyTrue: boolean;
  action: ControlText;
}): FilterSpec<object> => {
  const urlKey = toUrlKey(key);
  return {
    kind: 'filter-spec',
    signature: `${key}:boolean:${onlyTrue}`,
    controlIds: [key],
    urlParams: { [urlKey]: filterUrlState.boolean },
    resolve: (values, setValues) => {
      const stored = storedValue(values[urlKey] as Stored<boolean>, defaultValue);
      const value = onlyTrue && stored === false ? undefined : stored;
      const set = (next: Stored<boolean>) => setValues(() => ({ [urlKey]: next }));
      const hasDefault = defaultValue !== undefined;
      const common = { ...action, id: key, reset: () => set(undefined), isInitial: values[urlKey] === undefined };
      const control: FilterControl = onlyTrue
        ? {
            ...common,
            type: 'checkbox',
            value: value === true,
            onChange: (checked) => set(checked ? true : toStored(undefined, hasDefault))
          }
        : {
            ...common,
            type: 'boolean',
            value: value ?? null,
            onChange: (picked) => set(toStored(picked, hasDefault))
          };
      return brand({ controls: [control], apiInput: { [key]: value } });
    }
  };
};

const TEXT_PARAM = storedParam<string>(String, (value) => value.trim() || undefined);

/** Free text into a scalar param (an id from another system, a reference). Trimmed; empty sends nothing. */
export const textSpec = ({
  key,
  text
}: {
  key: string;
  text: ControlText & { placeholder?: string };
}): FilterSpec<object> => {
  const urlKey = toUrlKey(key);
  return {
    kind: 'filter-spec',
    signature: `${key}:text`,
    controlIds: [key],
    urlParams: { [urlKey]: TEXT_PARAM },
    resolve: (values, setValues) => {
      const value = storedValue(values[urlKey] as Stored<string>, undefined);
      return brand({
        controls: [
          {
            ...text,
            id: key,
            type: 'text',
            value: value ?? null,
            onChange: (picked) => setValues(() => ({ [urlKey]: picked?.trim() || undefined })),
            reset: () => setValues(() => ({ [urlKey]: undefined })),
            isInitial: values[urlKey] === undefined
          }
        ],
        apiInput: { [key]: value }
      });
    }
  };
};

/**
 * A spec under prefixed URL keys (`allocation-statuses`), for two lists on one page that filter the same param, or
 * a dialog over a list: builders key the URL by param, so without it each would filter the other. Not linkable:
 * `filterSearch` writes unprefixed keys.
 */
export const withUrlPrefix = <TParams, TControl extends FilterControl>(
  prefix: string,
  spec: FilterSpec<TParams, TControl, object>
): FilterSpec<TParams, TControl, object> => {
  const prefixed = (key: string) => `${prefix}-${key}`;
  const fromPrefixed = (values: UrlValues) =>
    Object.fromEntries(Object.keys(spec.urlParams).map((key) => [key, values[prefixed(key)]]));
  const toPrefixed = (values: UrlValues) =>
    Object.fromEntries(Object.entries(values).map(([key, value]) => [prefixed(key), value]));
  return {
    ...spec,
    signature: prefixed(spec.signature),
    urlParams: Object.fromEntries(Object.entries(spec.urlParams).map(([key, config]) => [prefixed(key), config])),
    resolve: (values, setValues) =>
      spec.resolve(fromPrefixed(values), (changes) => setValues((latest) => toPrefixed(changes(fromPrefixed(latest)))))
  };
};

/**
 * A composite filter on the same URL state as the builders: it declares its URL params (`filterUrlState`) and builds
 * controls + API params from their values. For filters the builders cannot express (several controls on one param,
 * one control writing two params, data-derived defaults), so none falls back to hand-rolled URL hooks.
 */
export const composeSpec = <TParams, TLink = object>(
  /** Keys the URL configs: include whatever the decoders depend on (loaded option ids), or they stay stale. */
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each composite owns its value types
  urlParams: Record<string, QueryParamConfig<any, any>>,
  resolve: (values: UrlValues, setValues: SetUrlValues) => FilterUnit<TParams>,
  controlIds: readonly string[] = []
): FilterSpec<TParams, FilterControl, TLink> => ({
  kind: 'filter-spec',
  signature: `compose:${name}:${Object.keys(urlParams).join(',')}`,
  controlIds,
  urlParams,
  resolve
});
